import { RefreshTokenError, tokenRefreshService } from "src/services/token-refresh.service";
import { useAuthStore } from "src/stores/auth-store";
import { logger } from "src/utils";

/**
 * Update the auth store with new token information
 */
async function updateStoreWithNewTokens(): Promise<void> {
  const { setAuthStoreState } = useAuthStore.getState();
  const { oktaAuthService } = await import("src/services/oktaAuth.service");
  const tokenInfo = await oktaAuthService.getAccessTokenWithExpiration();

  if (tokenInfo.accessToken && tokenInfo.expiresAt) {
    setAuthStoreState({
      accessToken: tokenInfo.accessToken,
      expiresAt: tokenInfo.expiresAt,
      isOktaLoginEnabled: true,
    });
  }
}

/**
 * Handle UNAUTHENTICATED errors with automatic token refresh
 *
 * @param executeRequest - Function to retry the original request
 * @param logPrefix - Prefix for log messages (e.g., "[Fetcher]" or "[FetcherNext]")
 * @returns The result of the retried request
 * @throws Error if refresh fails or request cannot be retried
 */
export async function handleUnauthenticatedError<TData>(
  executeRequest: () => Promise<TData>,
  logPrefix: string = "[Fetcher]"
): Promise<TData> {
  const { logout } = useAuthStore.getState();

  try {
    logger.info(`${logPrefix} UNAUTHENTICATED error, attempting token refresh...`);

    // Try to refresh tokens with retry for network errors
    const newTokens = await tokenRefreshService.refreshTokens();

    logger.info(`${logPrefix} Token refresh successful, retrying request...`);

    // Update store with new token info
    if (newTokens.accessToken && newTokens.expiresAt) {
      await updateStoreWithNewTokens();
    }

    // Retry the original request with new token
    return await executeRequest();
  } catch (error) {
    // Refresh failed - check error type
    if (error instanceof RefreshTokenError) {
      switch (error.type) {
        case "REFRESH_TOKEN_EXPIRED":
        case "REFRESH_TOKEN_INVALID":
          logger.error(`${logPrefix} Refresh token expired/invalid, user already logged out`);
          throw new Error("Session expired");

        case "NETWORK_ERROR":
          logger.error(`${logPrefix} Network error persisted after retries`);
          // For persistent network errors, logout the user
          await logout();
          throw new Error("Network error - session terminated");

        default:
          logger.error(`${logPrefix} Unknown refresh error: ${error.message}`);
          await logout();
          throw new Error("Authentication failed");
      }
    }

    // Unknown error type
    logger.error(`${logPrefix} Unexpected error during refresh: ${error}`);
    throw error;
  }
}

/**
 * Ensure access token is valid before making request
 * Refreshes token if expired or about to expire
 *
 * @param logPrefix - Prefix for log messages (e.g., "[Fetcher]" or "[FetcherNext]")
 * @throws Error if session has expired and user needs to re-authenticate
 */
export async function ensureValidToken(logPrefix: string = "[Fetcher]"): Promise<void> {
  const { shouldRefreshAccessToken, isOktaLoginEnabled } = useAuthStore.getState();

  // Only check for OKTA authenticated users
  if (!isOktaLoginEnabled) {
    return;
  }

  // Check if token needs refresh
  if (shouldRefreshAccessToken()) {
    logger.info(`${logPrefix} Access token expired or expiring soon, refreshing...`);

    try {
      const newTokens = await tokenRefreshService.refreshTokens();

      // Update store with new token info
      if (newTokens.accessToken && newTokens.expiresAt) {
        await updateStoreWithNewTokens();
      }

      logger.info(`${logPrefix} Pre-request token refresh successful`);
    } catch (error) {
      if (error instanceof RefreshTokenError) {
        if (error.type === "REFRESH_TOKEN_EXPIRED" || error.type === "REFRESH_TOKEN_INVALID") {
          // User already logged out by token refresh service
          throw new Error("Session expired");
        }
      }
      // For other errors, log and continue - the request might still work
      // or will trigger the 401 handling
      logger.error(`${logPrefix} Pre-request refresh failed: ${error}`);
    }
  }
}
